import { Button } from 'components'
import { motion } from 'framer-motion'
import { useTheme } from 'hooks'
import { Calendar, Check, ExternalLink } from 'lucide-react'
import { useBrandTimezone } from '../../common/hooks/use-brand-timezone'
import { formatScheduledDate } from '../../calendar/components/ScheduleDialog'

interface ScheduleButtonProps {
  /** ISO timestamp the post is scheduled for, or null if not yet scheduled. */
  scheduledAt: string | null | undefined
  /** Opens the schedule entry surface (ScheduleDialog or PostModal in Post mode). */
  onClick: () => void
  /** Truthy in Post-mode (multi-platform) — drives the primary variant. */
  postContext: boolean
  /**
   * One-shot flag that fires a brief acid pulse + scale animation right after
   * a successful schedule. Parent flips to true on PostModal onClose with
   * scheduled=true and auto-resets to false after the animation completes.
   */
  justScheduled?: boolean
  /** Disable scheduling — e.g. the active creative has already published. */
  disabled?: boolean
  /** When published, the live post URL — turns the CTA into a "Live" link. */
  livePermalink?: string
  /**
   * The post requires approval and isn't approved yet (review-mode autopilot).
   * Turns the CTA into a primary acid "Approve" action; click opens the schedule
   * surface where the user can optionally reschedule, then approve.
   */
  needsApproval?: boolean
  /**
   * Forwarded onto the inner `<Button>`. Used by `ScheduleSplitButton` to
   * square the button's right corner (`rounded-r-none`) so it seams cleanly
   * with the adjacent "Post now" caret. No effect on the standalone button.
   */
  className?: string
}

/**
 * The composer's Schedule button, with two visual states + a celebration.
 *
 * - Not scheduled → "Schedule"
 * - Scheduled in the future → "Scheduled for {date}" (same button, same handler;
 *   click reopens the schedule surface for reschedule).
 * - `justScheduled` → wraps the button in a one-shot acid glow + 1.04 scale
 *   pulse for ~600ms. Pure CSS-via-framer; the underlying button stays
 *   interactive so a fast user can re-tap during the pulse.
 *
 * Extracted from ComposerHeader's ~12 inline duplicates so the animation
 * lives in one place and platform-specific publish surfaces stay focused.
 */
export function ScheduleButton({
  scheduledAt,
  onClick,
  postContext,
  justScheduled = false,
  disabled = false,
  livePermalink,
  needsApproval = false,
  className,
}: ScheduleButtonProps) {
  // Render the schedule wall-clock in the business timezone so the CTA matches
  // the schedule modal, the toast, and the calendar (browser-local drifted).
  const tz = useBrandTimezone()
  const isScheduled = !!scheduledAt
  // Approval takes precedence over the scheduled label: a needs-approval post is
  // already scheduled, but the actionable thing is to approve it. Surface WHEN
  // it publishes right in the CTA ("Approve · Jun 17 at 9:00 AM") so the user
  // commits to a concrete date, not an abstract approval. Falls back to a plain
  // "Approve" if the schedule is somehow missing.
  const label = needsApproval
    ? isScheduled
      ? `Approve · ${formatScheduledDate(scheduledAt!, tz)}`
      : 'Approve'
    : isScheduled
      ? `Scheduled for ${formatScheduledDate(scheduledAt!, tz)}`
      : 'Schedule'
  // Acid primary CTA when approval is pending, or in Post mode; tertiary otherwise.
  const variant = needsApproval || postContext ? 't3-primary' : 't3-tertiary'

  // The pulse color must match the active accent: lime on dark, Pool cyan on
  // user-light. framer-motion interpolates boxShadow numerically, so it can't
  // read `var(--acid)` — resolve the rgb triplet from the theme instead.
  // (Hook stays above any early return — rules-of-hooks.)
  const { theme } = useTheme()
  const glow = theme === 'light' ? '36,211,239' : '198,255,61'

  // Published → the CTA is a "Live" link to the post (or a static "Live" pill
  // when no permalink is available), never a schedule control.
  if (disabled) {
    const liveClass =
      'inline-flex items-center gap-1.5 rounded-[10px] border border-green-200 bg-green-50 px-4 py-2 text-sm font-medium text-green-700 transition-colors hover:bg-green-100'
    return livePermalink ? (
      <a href={livePermalink} target="_blank" rel="noopener noreferrer" className={liveClass}>
        <Check size={14} />
        Live
        <ExternalLink size={12} className="opacity-70" />
      </a>
    ) : (
      <span className={liveClass}>
        <Check size={14} />
        Live
      </span>
    )
  }

  return (
    <motion.div
      animate={
        justScheduled && !disabled
          ? {
              scale: [1, 1.04, 1],
              boxShadow: [
                `0 0 0 rgba(${glow},0)`,
                `0 0 24px rgba(${glow},0.65)`,
                `0 0 0 rgba(${glow},0)`,
              ],
            }
          : { scale: 1, boxShadow: `0 0 0 rgba(${glow},0)` }
      }
      transition={{
        duration: 0.6,
        ease: 'easeOut',
        // Spell out per-key behavior so boxShadow's three-stop interpolation
        // stays sync'd with the scale pulse rather than racing it.
        times: [0, 0.5, 1],
      }}
      className="inline-flex rounded-[8px]"
    >
      <Button variant={variant} size="sm" onClick={onClick} className={className}>
        {needsApproval ? <Check size={14} /> : <Calendar size={14} />}
        {label}
      </Button>
    </motion.div>
  )
}
